import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, ShieldCheck, MapPinOff, Play } from 'lucide-react';
import { SafeImage } from '@/components/ui/safe-image';
import { MediaItem } from '@property/shared';
import { cn } from '@/lib/utils';

interface MediaCardProps {
  item: MediaItem;
  onSelect: () => void;
  isBroken?: boolean;
  onLoadError?: () => void;
}

function formatCoord(value: number, posLabel: string, negLabel: string): string {
  const deg = Math.abs(value);
  const d = Math.floor(deg);
  const minFull = (deg - d) * 60;
  const m = Math.floor(minFull);
  const s = ((minFull - m) * 60).toFixed(1);
  const dir = value >= 0 ? posLabel : negLabel;
  return `${d}°${m}'${s}"${dir}`;
}

export function MediaCard({ item, onSelect, isBroken, onLoadError }: MediaCardProps) {
  const hasCoords = item.latitude != null && item.longitude != null;

  return (
    <Card
      className={cn(
        'group overflow-hidden border-border/40 bg-background/50 backdrop-blur-md shadow-sm transition-all p-0 gap-0',
        !isBroken && 'hover:shadow-lg',
      )}
    >
      <div
        className={cn(
          'relative aspect-video overflow-hidden bg-muted',
          !isBroken && 'cursor-zoom-in',
        )}
        onClick={() => !isBroken && onSelect()}
      >
        {item.type === 'video' ? (
          <video
            src={item.url || ''}
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out',
              !isBroken && 'grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105',
            )}
            muted
            preload="metadata"
          />
        ) : (
          <SafeImage
            src={item.url || ''}
            alt={item.label || ''}
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out',
              !isBroken && 'grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105',
            )}
            onErrorStateChange={(err) => err && onLoadError?.()}
          />
        )}
        {!isBroken && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
            {item.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="rounded-full bg-black/40 p-3 backdrop-blur-sm">
                  {/* eslint-disable-next-line local/no-raw-colors -- icon on dark video overlay */}
                  <Play className="h-6 w-6 text-white fill-white" />
                </div>
              </div>
            )}

            <div className="absolute top-4 left-4">
              {/* eslint-disable-next-line local/no-raw-colors -- text on image overlay */}
              <Badge className="bg-white/10 backdrop-blur-md border hover:bg-white/20 text-white text-[9px] px-2 py-0.5 border-white/20 uppercase tracking-widest font-bold">
                Property Record
              </Badge>
            </div>

            {/* eslint-disable-next-line local/no-raw-colors -- text on image overlay */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
              <div className="space-y-1">
                <p className="text-xs font-bold tracking-wide uppercase opacity-70">{item.type}</p>
                <p className="text-lg font-black tracking-tight">{item.label}</p>
              </div>
            </div>
          </>
        )}
      </div>

      <CardContent className="p-0 bg-muted/20">
        {/* GPS coordinates strip */}
        {hasCoords ? (
          <div className="px-4 py-3 border-b border-border/30">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex-shrink-0">
                <MapPin className="h-3.5 w-3.5 text-sidebar-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-sidebar-primary mb-1">
                  GPS Location Verified
                </p>
                <div className="font-mono text-[11px] leading-relaxed text-foreground/80 tabular-nums">
                  <span className="block">{formatCoord(item.latitude!, 'N', 'S')}</span>
                  <span className="block">{formatCoord(item.longitude!, 'E', 'W')}</span>
                </div>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 border-b border-border/30">
            <div className="flex items-center gap-2.5">
              <MapPinOff className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" />
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/40">
                No GPS data
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium uppercase tracking-wider">
            <ShieldCheck className="h-3 w-3" /> Tamper Proof
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
